// Admin › Settings › Referral Program — rewards entry fees (rewards part 3).
// Who owes, paid, was waived or refunded; waive a due fee with a reason.
// Refunds are made in Stripe and arrive through the webhook.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  adminEntryFeesKey,
  fetchAdminEntryFees,
  waiveEntryFee,
  type EntryFeeAdminRow,
} from '../../../api/entryFee'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Card } from '../../../components/ui/card'
import { DataTableShell } from '../../../components/ui/data-table-shell'
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog'
import { Dialog } from '../../../components/ui/dialog.lib'
import { Field } from '../../../components/ui/field'
import { useT } from '../../../i18n'
import {
  rewardsEntryFeesI18n,
  type RewardsEntryFeesStrings,
} from '../../../i18n/admin/rewards-entry-fees'
import { formatCurrency } from '../../../lib/currency'
import { formatDate } from '../../../lib/datetime'

const TEXTAREA_CLASS =
  'w-full rounded-[var(--field-radius)] border border-[var(--field-border)] bg-[var(--field-bg)] px-[var(--field-padding-x)] py-2 text-[length:var(--field-font-size)] text-[var(--field-text)] placeholder:text-[var(--field-placeholder)] focus-visible:border-[var(--field-focus-border)] focus-visible:outline-none focus-visible:shadow-[var(--field-focus-shadow)]'

const BADGE = {
  due: 'warning',
  paid: 'success',
  waived: 'neutral',
  refunded: 'destructive',
} as const

function fmtDate(iso: string | null): string {
  return iso ? formatDate(iso, { year: 'numeric', month: 'short', day: 'numeric' }) : '—'
}

function WaiveDialog({
  row,
  onClose,
  t,
}: {
  row: EntryFeeAdminRow
  onClose: () => void
  t: RewardsEntryFeesStrings
}) {
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')
  const [touched, setTouched] = useState(false)
  const ok = reason.trim().length >= 3
  const mutation = useMutation({
    mutationFn: () => waiveEntryFee(row.org_id, reason.trim()),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: adminEntryFeesKey })
      onClose()
    },
  })
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t.waiveTitle(row.org_name)}</DialogTitle>
          <DialogDescription>{t.waiveBody}</DialogDescription>
        </DialogHeader>
        <Field
          id="entry-fee-waive-reason"
          label={t.reasonLabel}
          required
          error={touched && !ok ? t.reasonRequired : undefined}
        >
          <textarea
            rows={3}
            maxLength={500}
            className={TEXTAREA_CLASS}
            placeholder={t.reasonPlaceholder}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
          />
        </Field>
        {mutation.isError && (
          <p role="alert" className="text-sm text-[var(--color-danger)]">
            {(mutation.error as Error).message}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t.cancel}
          </Button>
          <Button
            variant="primary"
            disabled={mutation.isPending}
            onClick={() => {
              setTouched(true)
              if (ok) mutation.mutate()
            }}
          >
            {mutation.isPending ? t.saving : t.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function RewardsEntryFeesCard() {
  const t = useT(rewardsEntryFeesI18n)
  const query = useQuery({ queryKey: adminEntryFeesKey, queryFn: fetchAdminEntryFees })
  const [waiving, setWaiving] = useState<EntryFeeAdminRow | null>(null)
  const rows = query.data?.items ?? []

  return (
    <Card className="space-y-3 p-4">
      <div>
        <h2 className="text-base font-semibold text-[var(--color-text)]">{t.title}</h2>
        <p className="mt-0.5 text-sm text-[var(--color-text-muted)]">{t.subtitle}</p>
      </div>
      {query.isLoading ? (
        <p className="text-sm text-[var(--color-text-muted)]">{t.loading}</p>
      ) : query.isError ? (
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span role="alert" className="text-[var(--color-danger)]">
            {t.loadError}
          </span>
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            {t.retry}
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-[var(--color-text-muted)]">{t.empty}</p>
      ) : (
        <DataTableShell variant="embedded">
          <table>
            <thead>
              <tr>
                <th>{t.columns.org}</th>
                <th>{t.columns.state}</th>
                <th className="text-right">{t.columns.amount}</th>
                <th>{t.columns.when}</th>
                <th className="text-right">{t.columns.actions}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const state = row.state === 'not_required' ? 'due' : row.state
                return (
                  <tr key={row.org_id}>
                    <td className="font-medium">{row.org_name}</td>
                    <td>
                      <Badge variant={BADGE[state]}>{t.states[state]}</Badge>
                      {(row.failure_reason || row.waived_reason) && (
                        <div className="mt-0.5 text-xs text-[var(--color-text-muted)]">
                          {row.waived_reason ?? row.failure_reason}
                        </div>
                      )}
                    </td>
                    <td className="text-right tabular-nums">
                      {formatCurrency(Number(row.amount_usd), 'USD')}
                    </td>
                    <td className="tabular-nums">
                      {fmtDate(row.refunded_at ?? row.paid_at ?? row.created_at)}
                    </td>
                    <td className="text-right">
                      {row.state === 'due' && (
                        <Button variant="outline" size="sm" onClick={() => setWaiving(row)}>
                          {t.waive}
                        </Button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </DataTableShell>
      )}
      {waiving && <WaiveDialog row={waiving} onClose={() => setWaiving(null)} t={t} />}
    </Card>
  )
}
